import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  exchangeCodeForTokens,
  refreshAccessToken,
  validateSession,
  ValidateSessionError
} from 'redux/features/bccAuth/bccAuthApiThunks'
import { inIdle, inProgress, successResponse, failedResponse, ApiStatus } from 'redux/toolkit/api'
import {
  getStoredAccessToken,
  clearAccessToken as clearStoredAccessToken,
  storeOAuthState,
  clearOAuthState,
  getStoredSessionSecret,
  storeSessionSecret,
  clearSessionSecret,
  isTokenExpired
} from 'lib/oauth2/bccOAuth2Utils'

export interface BccAuthState {
  accessToken?: string
  expiresAt?: number
  sessionSecret?: string
  sessionExpirationTime?: number
  oauthState?: string
  api: {
    tokenExchangeStatus: ApiStatus
    refreshTokenStatus: ApiStatus
    sessionValidationStatus: ApiStatus
  }
}

// Static initial state - hydration from sessionStorage is done via hydrateFromStorage action
// to avoid circular dependencies with appConfig at module load time
export const INITIAL_STATE: BccAuthState = {
  accessToken: undefined,
  expiresAt: undefined,
  sessionSecret: undefined,
  sessionExpirationTime: undefined,
  oauthState: undefined,
  api: {
    tokenExchangeStatus: inIdle,
    refreshTokenStatus: inIdle,
    sessionValidationStatus: inIdle
  }
}

/* eslint-disable no-param-reassign */
export const bccAuthSlice = createSlice({
  name: 'BCC_AUTH',
  initialState: INITIAL_STATE,
  reducers: {
    reset: () => ({
      ...INITIAL_STATE,
      accessToken: undefined,
      expiresAt: undefined,
      sessionSecret: undefined,
      sessionExpirationTime: undefined,
      oauthState: undefined
    }),
    setOAuthState: (state, action: PayloadAction<string>) => {
      state.oauthState = action.payload
      storeOAuthState(action.payload)
    },
    clearOAuthStateAction: state => {
      state.oauthState = undefined
      clearOAuthState()
    },
    setAccessToken: (
      state,
      action: PayloadAction<{ accessToken: string; expiresAt: number; sessionSecret?: string }>
    ) => {
      state.accessToken = action.payload.accessToken
      state.expiresAt = action.payload.expiresAt
      state.sessionSecret = action.payload.sessionSecret
    },
    clearAccessToken: state => {
      state.accessToken = undefined
      state.expiresAt = undefined
      state.sessionSecret = undefined
      clearStoredAccessToken()
      clearSessionSecret()
    },
    resetTokenExchange: state => {
      state.api.tokenExchangeStatus = INITIAL_STATE.api.tokenExchangeStatus
    },
    resetRefreshToken: state => {
      state.api.refreshTokenStatus = INITIAL_STATE.api.refreshTokenStatus
    },
    resetSessionValidation: state => {
      state.api.sessionValidationStatus = INITIAL_STATE.api.sessionValidationStatus
    },
    hydrateFromStorage: state => {
      // Only hydrate if we haven't already (accessToken is still undefined)
      if (state.accessToken !== undefined) {
        return
      }
      const storedToken = getStoredAccessToken()
      const storedSessionSecret = getStoredSessionSecret()

      if (storedToken.token && storedToken.expiresAt && !isTokenExpired(storedToken.expiresAt)) {
        state.accessToken = storedToken.token
        state.expiresAt = storedToken.expiresAt
      }
      if (storedSessionSecret) {
        state.sessionSecret = storedSessionSecret
      }
    }
  },
  extraReducers: builder => {
    builder
      // exchangeCodeForTokens
      .addCase(exchangeCodeForTokens.pending, state => {
        state.api.tokenExchangeStatus = inProgress
      })
      .addCase(exchangeCodeForTokens.fulfilled, (state, action) => {
        state.api.tokenExchangeStatus = successResponse
        state.accessToken = action.payload.accessToken
        state.expiresAt = action.payload.expiresAt
        state.sessionSecret = action.payload.sessionSecret
        state.oauthState = undefined
        clearOAuthState()
        // Reset error statuses on successful token exchange
        state.api.refreshTokenStatus = inIdle
        state.api.sessionValidationStatus = inIdle
        if (action.payload.sessionSecret) {
          storeSessionSecret(action.payload.sessionSecret)
        }
      })
      .addCase(exchangeCodeForTokens.rejected, (state, action) => {
        state.api.tokenExchangeStatus = failedResponse(action.payload?.message)
        state.oauthState = undefined
        clearOAuthState()
      })

      // refreshAccessToken
      .addCase(refreshAccessToken.pending, state => {
        state.api.refreshTokenStatus = inProgress
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.api.refreshTokenStatus = successResponse
        state.accessToken = action.payload.accessToken
        state.expiresAt = action.payload.expiresAt
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.api.refreshTokenStatus = failedResponse(action.payload)
        state.accessToken = undefined
        state.expiresAt = undefined
        state.sessionSecret = undefined
        clearStoredAccessToken()
        clearSessionSecret()
      })

      // validateSession
      .addCase(validateSession.pending, state => {
        state.api.sessionValidationStatus = inProgress
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.api.sessionValidationStatus = successResponse
        state.sessionExpirationTime = action.payload.expectedExpirationTime
      })
      .addCase(validateSession.rejected, (state, action) => {
        const error = action.payload as ValidateSessionError
        if (error?.isTransient) {
          // Transient error (5xx / network) — keep existing auth state,
          // let access token's own expiry be the fallback
          state.api.sessionValidationStatus = inIdle
        } else {
          // Definitive session invalidity (4xx) — clear all auth state
          state.api.sessionValidationStatus = failedResponse(error?.message || 'Session validation failed')
          state.accessToken = undefined
          state.expiresAt = undefined
          state.sessionSecret = undefined
          state.sessionExpirationTime = undefined
          clearStoredAccessToken()
          clearSessionSecret()
        }
      })
  }
})
/* eslint-enable no-param-reassign */

export const {
  reset,
  setOAuthState,
  clearOAuthStateAction,
  setAccessToken,
  clearAccessToken,
  resetTokenExchange,
  resetRefreshToken,
  resetSessionValidation,
  hydrateFromStorage
} = bccAuthSlice.actions

export { exchangeCodeForTokens, refreshAccessToken, validateSession }

export default bccAuthSlice.reducer
